import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { normalizeUiLang } from '../../utils/localePreference';
import { digitalCardStyles } from './digitalCardStyles';
import DigitalCardNav from './DigitalCardNav';
import DigitalCardFooter from './DigitalCardFooter';

const DigitalCardContactPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const dispose = applySeo({
      title: t('dcl_contact_seo_title', { defaultValue: 'Contact TCards' }),
      description: t('dcl_contact_seo_desc', {
        defaultValue: 'Have a question, need support or want to learn more about TCards? Get in touch with us.',
      }),
      image: ogBrandImageUrl(origin, 'site'),
      url: `${origin}/contact`,
      canonicalUrl: `${origin}/contact`,
      siteName: 'TCards',
    });
    return dispose;
  }, [t, resolvedLang]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      setMsg({ type: 'err', text: t('dcl_contact_fill_required', { defaultValue: 'Please fill in name, email and message.' }) });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardOwnerId: 'homepage',
          cardOwnerEmail: 'info@tcards.org',
          cardOwnerName: 'TCards Digital Card',
          fullName: form.name,
          email: form.email,
          phoneNumber: form.phone || undefined,
          message: `[${form.subject || 'General inquiry'}]\n${form.message}`,
          source: 'digital_card_contact',
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error('Failed');
      setMsg({ type: 'ok', text: t('dcl_contact_success', { defaultValue: 'Thank you — your message has been sent. We will get back to you soon.' }) });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setMsg({ type: 'err', text: t('dcl_contact_error', { defaultValue: 'Something went wrong. Please try again or email info@tcards.org directly.' }) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dcl-root">
      <style>{digitalCardStyles}</style>
      <DigitalCardNav activePath="/contact" />

      <section className="dcl-section">
        <div className="dcl-container dcl-container--narrow">
          <h1 className="dcl-section-title">
            {t('dcl_contact_title', { defaultValue: 'Contact TCards' })}
          </h1>
          <p className="dcl-section-sub">
            {t('dcl_contact_sub', {
              defaultValue: 'Have a question, need support or want to learn more about TCards? Get in touch with us.',
            })}
          </p>
          <p style={{ fontSize: 15, color: '#64748B', marginBottom: 24 }}>
            {t('dcl_contact_email_label', { defaultValue: 'Email' })}:{' '}
            <a href="mailto:info@tcards.org" style={{ color: '#2563EB', fontWeight: 600 }}>info@tcards.org</a>
          </p>

          <form className="dcl-form" onSubmit={handleSubmit}>
            <input
              className="dcl-input"
              placeholder={t('dcl_contact_name', { defaultValue: 'Name' })}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <input
              className="dcl-input"
              type="email"
              placeholder={t('dcl_contact_email', { defaultValue: 'Email' })}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
            />
            <input
              className="dcl-input"
              placeholder={t('dcl_contact_phone', { defaultValue: 'Phone / WhatsApp' })}
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <input
              className="dcl-input"
              placeholder={t('dcl_contact_subject', { defaultValue: 'Subject' })}
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            />
            <textarea
              className="dcl-textarea"
              placeholder={t('dcl_contact_message', { defaultValue: 'Message' })}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              required
            />
            <button type="submit" className="dcl-btn dcl-btn--primary" disabled={submitting}>
              {submitting ? t('dcl_contact_sending', { defaultValue: 'Sending…' }) : t('dcl_contact_send', { defaultValue: 'Send Message' })}
            </button>
            {msg && (
              <div className={`dcl-form-msg dcl-form-msg--${msg.type}`}>{msg.text}</div>
            )}
          </form>
        </div>
      </section>

      <DigitalCardFooter />
    </div>
  );
};

export default DigitalCardContactPage;
